import React, { useContext, useState } from "react"
import PropTypes from "prop-types"
import { Box, Heading, Checkbox, Input, Text } from "@chakra-ui/react"
import { FilterContext } from "./filterContext"
import { FormattedMessage, useIntl } from "gatsby-plugin-intl"

const Filter = () => {
  const bikeFilterContext = useContext(FilterContext)
  const [minPrice, setMinPrice] = useState("")
  const [maxPrice, setMaxPrice] = useState("")
  const {
    allBikes,
    filters,
    setFilters,
    setFilteredBikes,
    filteredBikes,
  } = bikeFilterContext
  if (!allBikes || allBikes.length === 0) {
    return null
  }

  const intl = useIntl()
  const renderBikes = Object.keys(filters).length > 0 ? filteredBikes : allBikes
  const properties = {
    brands: {},
    inStock: null,
    engine: {},
    battery: {},
  }
  allBikes.forEach(b => {
    if (b.brand) properties.brands[b.brand] = true
    if (b.inStock) properties.inStock = true
    if (b.engine) {
      if (!properties.engine) properties.engine = {}
      properties.engine[b.engine] = true
    }
    if (b.tags) {
      if (!properties.tags) properties.tags = {}
      b.tags?.split(", ").map(tag => (properties.tags[tag] = true))
    }
    if (b.prices?.prices.length > 0) {
      properties.price = true
      if (!properties.battery) properties.battery = {}
      b.prices?.prices.forEach(price => {
        if (price.battery !== null) properties.battery[price.battery] = true
      })
    }
  })

  const filter = async newFilters => {
    await setFilteredBikes(
      allBikes.filter(b => {
        if (newFilters === {}) return true
        if (newFilters.brands && !newFilters.brands[b.brand]) return false
        if (newFilters.inStock && !b.inStock) return false
        if (newFilters.engine && !newFilters.engine[b.engine]) return false
        if (newFilters.tags) {
          let hasValidTag = false
          b.tags?.split(", ").forEach(tag => {
            if (newFilters.tags[tag]) hasValidTag = true
          })
          return hasValidTag
        }
        if (newFilters.battery) {
          let hasValidBattery = false
          b.prices.prices.forEach(price => {
            if (newFilters.battery[price.battery]) hasValidBattery = true
          })
          return hasValidBattery
        }
        if (
          newFilters.minPrice &&
          !b.prices.prices.find(p => {
            return p.price >= Number(newFilters.minPrice)
          })
        )
          return false
        if (
          newFilters.maxPrice &&
          !b.prices.prices.find(p => p.price <= Number(newFilters.maxPrice))
        )
          return false
        return true
      })
    )
  }

  const toggleInStock = async () => {
    const newFilters = {
      ...filters,
    }
    if (newFilters.inStock) delete newFilters.inStock
    else newFilters.inStock = true
    await setFilters(newFilters)
    await filter(newFilters)
  }

  const toggleFilter = async (key, value) => {
    const exists = filters[key] && Object.keys(filters[key]).includes(value)
    const newFilters = {
      ...filters,
    }
    if (exists) delete newFilters[key][value]
    else
      newFilters[key] = {
        ...filters[key],
        [value]: true,
      }
    if (newFilters[key] && Object.keys(newFilters[key]).length === 0)
      delete newFilters[key]
    await setFilters(newFilters)
    await filter(newFilters)
  }

  const handleMinPrice = async e => {
    const val = e.target.value.replace(/\D/g, "")
    setMinPrice(val === "" ? "" : Number(val))
    const newFilters = {
      ...filters,
    }
    if (val === "" && newFilters.minPrice) delete newFilters.minPrice
    else newFilters.minPrice = val
    await setFilters(newFilters)
    await filter(newFilters)
  }

  const handleMaxPrice = async e => {
    const val = e.target.value.replace(/\D/g, "")
    setMaxPrice(val === "" ? "" : Number(val))
    const newFilters = {
      ...filters,
    }
    if (val === "" && newFilters.maxPrice) delete newFilters.maxPrice
    else newFilters.maxPrice = val
    await setFilters(newFilters)
    await filter(newFilters)
  }

  return (
    <Box marginBottom="100px">
      <Text as="i">
        <FormattedMessage id="filterHelper" />
      </Text>
      {/* Brand */}
      <Box marginBottom="3">
        <Heading size="md" marginTop="5" marginBottom="2">
          <FormattedMessage id="brand" />
        </Heading>
        {Object.keys(properties.brands).map(brand => (
          <Box key={brand}>
            <Checkbox
              isChecked={filters.brands && filters.brands[brand]}
              onChange={() => toggleFilter("brands", brand)}
            >
              {`${brand} (${
                renderBikes.filter(b => b.brand === brand).length
              })`}
            </Checkbox>
          </Box>
        ))}
      </Box>
      {properties.tags && (
        <Box marginBottom="3">
          <Heading size="md" marginTop="5" marginBottom="2">
            <FormattedMessage id="tags" />
          </Heading>
          {Object.keys(properties.tags).map(tag => (
            <Box key={tag}>
              <Checkbox
                isChecked={filters.tags && filters.tags[tag]}
                onChange={() => toggleFilter("tags", tag)}
              >
                {`${tag} (${
                  renderBikes.filter(b => b.tags?.split(", ").includes(tag))
                    .length
                })`}
              </Checkbox>
            </Box>
          ))}
        </Box>
      )}
      {properties.inStock && (
        <Box marginBottom="3">
          <Heading size="md" marginTop="5" marginBottom="2">
            <FormattedMessage id="inStock" />
          </Heading>
          <Checkbox isChecked={filters.inStock} onChange={toggleInStock}>
            <FormattedMessage id="onlyInStock" />{" "}
            {`(${renderBikes.filter(b => b.inStock).length})`}
          </Checkbox>
        </Box>
      )}
      {properties.engine && (
        <Box marginBottom="3">
          <Heading size="md" marginTop="5" marginBottom="2">
            <FormattedMessage id="motor" />
          </Heading>
          {Object.keys(properties.engine)
            .sort((a, b) => a.localeCompare(b))
            .map(engine => (
              <Box key={engine}>
                <Checkbox
                  isChecked={filters.engine && filters.engine[engine]}
                  onChange={() => toggleFilter("engine", engine)}
                >
                  {`${engine} (${
                    renderBikes.filter(b => b.engine === engine).length
                  })`}
                </Checkbox>
              </Box>
            ))}
        </Box>
      )}
      {properties.battery && (
        <Box marginBottom="3">
          <Heading size="md" marginTop="5" marginBottom="2">
            <FormattedMessage id="battery" />
          </Heading>
          {Object.keys(properties.battery)
            .sort((a, b) => {
              const aNum = Number(a.replace("Wh", ""))
              const bNum = Number(b.replace("Wh", ""))
              if (!isNaN(aNum) && !isNaN(bNum)) {
                return aNum - bNum
              }
            })
            .map(battery => (
              <Box key={battery}>
                <Checkbox
                  isChecked={filters.battery && filters.battery[battery]}
                  onChange={() => toggleFilter("battery", battery)}
                >
                  {`${battery} (${
                    renderBikes.filter(b =>
                      b.prices.prices.find(p => p.battery === battery)
                    ).length
                  })`}
                </Checkbox>
              </Box>
            ))}
        </Box>
      )}
      {properties.price && (
        <Box marginBottom="3">
          <Heading size="md" marginTop="5" marginBottom="2">
            <FormattedMessage id="price" />
          </Heading>
          <Input
            value={minPrice}
            onChange={handleMinPrice}
            placeholder={intl.formatMessage({ id: "minPrice" })}
            marginBottom="1"
          />
          <Input
            value={maxPrice}
            onChange={handleMaxPrice}
            placeholder={intl.formatMessage({ id: "maxPrice" })}
          />
        </Box>
      )}
    </Box>
  )
}

Filter.propTypes = {
  bikes: PropTypes.array,
}

export default Filter
